/**
 * ProgramDetailsFaqSection (program-details) — the experiment's own FAQ shell.
 *
 * The shared `@educator/components/Faq` section is also rendered by the
 * enrollment page, the Status Portal and the subject pages, so it can't be
 * restyled for this experiment without moving those. This shell puts the same
 * accordion on the experiment's own `Container` and header pattern instead.
 * Content still comes from `useProgramFaqItems` — the same source every other
 * surface reads.
 */
import { useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useProgramFaqItems } from '@educator/components/Faq'
import { Container } from '@educator/components/ProgramSections/Container'
import { useFireEvent } from '@educator/tracking/educatorTracking'
import { IconDown, IconUp } from '@mc/educator-design-system'

export const ProgramDetailsFaqSection = () => {
  const { headline, items } = useProgramFaqItems()
  const fireEvent = useFireEvent()
  const shouldReduceMotion = useReducedMotion()
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0)

  const handleToggle = (index: number) => {
    const isExpanding = expandedIndex !== index

    if (isExpanding) {
      fireEvent('educatorSignClick', {
        action: items[index].question,
        location: 'faq',
      })
    }

    setExpandedIndex(isExpanding ? index : null)
  }

  return (
    <section id='faq' data-section='faq' className='w-full py-6 md:py-12'>
      <Container>
        <div className='flex flex-col gap-6 md:gap-4'>
          <header className='flex flex-col gap-3'>
            <div className='border-t border-edu-stroke-default' />
            <div className='flex items-center justify-between md:min-h-12'>
              <h2 className='m-0 text-xl leading-tight font-semibold text-edu-fg-primary md:text-title-3 md:leading-title-3'>
                {headline}
              </h2>
            </div>
          </header>

          <div className='flex flex-col'>
            {items.map((item, index) => {
              const isExpanded = expandedIndex === index

              return (
                <div
                  key={item.question}
                  className='flex w-full flex-col gap-5 border-t border-white/20 py-5'
                >
                  <button
                    type='button'
                    className='flex w-full cursor-pointer items-center gap-5'
                    onClick={() => handleToggle(index)}
                    aria-expanded={isExpanded}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <span className='flex-1 text-left text-[14px] leading-[1.1] tracking-[0.56px] text-white'>
                      {item.question}
                    </span>
                    <span className='text-white'>
                      {isExpanded ? (
                        <IconUp className='size-3.5!' />
                      ) : (
                        <IconDown className='size-3.5!' />
                      )}
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        id={`faq-answer-${index}`}
                        initial={
                          shouldReduceMotion ? false : { height: 0, opacity: 0 }
                        }
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={
                          shouldReduceMotion
                            ? undefined
                            : { height: 0, opacity: 0 }
                        }
                        transition={
                          shouldReduceMotion
                            ? { duration: 0 }
                            : { duration: 0.3, ease: 'easeInOut' }
                        }
                        className='overflow-hidden'
                      >
                        <div className='py-3 pr-10'>
                          <p className='m-0 text-[14px] leading-[1.2] tracking-[0.56px] whitespace-pre-line text-white'>
                            {item.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}

            <div className='w-full border-t border-white/20' />
          </div>
        </div>
      </Container>
    </section>
  )
}
